import { any, array, bool, func, oneOf, string } from 'prop-types'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import ReactSelect, { components } from 'react-select'
import styled from 'styled-components'
const StyledClearIndicator = styled.div`
	align-items: center;
	cursor: pointer;
	display: flex;
	height: 23px;
	justify-content: center;
	width: 23px;
`
const ClearIndicator = props => (
	<StyledClearIndicator onClick={props.clearValue}>
		<svg fill='none' height='16' viewBox='0 0 16 16' width='16'>
			<path
				d='M12 4L4 12'
				stroke='#949494'
				strokeLinecap='round'
				strokeLinejoin='round'
				strokeWidth='2'
			/>
			<path
				d='M4 4L12 12'
				stroke='#949494'
				strokeLinecap='round'
				strokeLinejoin='round'
				strokeWidth='2'
			/>
		</svg>
	</StyledClearIndicator>
)
const Input = props => (
	<components.Input {...props} data-cy={props?.selectProps?.['data-cy']} />
)
const Control = props => (
	<components.Control
		{...props}
		innerProps={Object.assign(
			{},
			props?.innerProps,
			props?.selectProps?.isSearchable
				? {}
				: { 'data-cy': props?.selectProps?.['data-cy'] }
		)}
	/>
)
const Option = props => (
	<components.Option
		{...props}
		innerProps={Object.assign({}, props?.innerProps, {
			'data-cy': props?.selectProps?.['data-cy'] + '-' + props?.value,
		})}
	/>
)
const defaultOptions = {
	className: 'select-input',
	isMulti: false,
	maxMenuHeight: 250,
	menuPlacement: 'auto',
	menuPortalTarget: document.body,
	components: {
		ClearIndicator,
		Control,
		IndicatorSeparator: () => null,
		Input,
		Option,
	},
}
const styles = {
	valueContainer: (styles, props) => ({
		...styles,
		display: 'flex',
		height:
			props?.selectProps?.size === 'large'
				? 50
				: props?.selectProps?.size === 'small'
				? 34
				: props?.selectProps?.size === 'extra-small'
				? 30
				: 42,
		padding:
			props?.selectProps?.size === 'large'
				? '13px 0 13px 15px'
				: props?.selectProps?.size === 'small'
				? '5px 0 5px 15px'
				: props?.selectProps?.size === 'extra-small'
				? '5px 0 5px 15px'
				: '9px 0 9px 15px',
	}),
	singleValue: (styles, { isDisabled, selectProps }) => ({
		...styles,
		color: isDisabled ? '#717171' : '#252a3b',
		fontWeight: 500,
		left: 0,
		margin: 0,
		position: 'absolute',
		top: 0,
		fontSize:
			selectProps?.size === 'large'
				? 18
				: selectProps?.size === 'small'
				? 14
				: selectProps?.size === 'extra-small'
				? 12
				: 16,
		padding:
			selectProps?.size === 'large'
				? '14px 8px 14px 18px'
				: selectProps?.size === 'small'
				? '8px 8px 8px 12px'
				: selectProps?.size === 'extra-small'
				? '6px 6px 6px 10px'
				: '11px 8px 11px 16px',
		'.dark &': {
			color: isDisabled ? '#aaaaaa' : '#e1e1e1',
		},
	}),
	placeholder: (styles, props) => ({
		...styles,
		fontWeight: 500,
		margin: 0,
		position: 'absolute',
		left: 0,
		top: '1.8px',
		color: '#717171',
		fontSize:
			props?.selectProps?.size === 'large'
				? 18
				: props?.selectProps?.size === 'small'
				? 14
				: props?.selectProps?.size === 'extra-small'
				? 12
				: 16,
		padding:
			props?.selectProps?.size === 'large'
				? '12px 8px 12px 16px'
				: props?.selectProps?.size === 'small'
				? '4px 8px 4px 16px'
				: props?.selectProps?.size === 'extra-small'
				? '4px 8px 4px 10px'
				: '8px 8px 8px 16px',
		'.dark &': {
			color: '#e1e1e1',
		},
	}),
	input: (styles, { isDisabled, selectProps }) => ({
		...styles,
		color: isDisabled ? '#717171' : '#000000',
		fontWeight: 500,
		position: 'absolute',
		left:
			selectProps?.size === 'large'
				? 16
				: selectProps?.size === 'small'
				? 10
				: selectProps?.size === 'extra-small'
				? 6
				: 14,
		top:
			selectProps?.size === 'large'
				? 10
				: selectProps?.size === 'small'
				? 4
				: selectProps?.size === 'extra-small'
				? 2
				: 7,
		fontSize:
			selectProps?.size === 'large'
				? 18
				: selectProps?.size === 'small'
				? 14
				: selectProps?.size === 'extra-small'
				? 12
				: 16,
		'.dark &': {
			color: isDisabled ? '#aaaaaa' : '#e1e1e1',
		},
	}),
	indicatorsContainer: (styles, props) => ({
		...styles,
		padding:
			props?.selectProps?.size === 'large'
				? '0 16px 0 10px'
				: props?.selectProps?.size === 'small'
				? '0 8px 0 6px'
				: props?.selectProps?.size === 'extra-small'
				? '0 4px 0 3px'
				: '0 12px 0 8px',
	}),
	dropdownIndicator: styles => ({
		...styles,
		alignItems: 'center',
		backgroundColor: 'transparent',
		borderRadius: 12,
		color: '#808080',
		display: 'flex',
		height: 23,
		justifyContent: 'center',
		margin: 0,
		padding: 0,
		width: 23,
		':hover': {
			color: '#808080',
		},
		svg: {
			width: 18,
		},
		'.dark &': {
			color: '#aaaaaa',
			':hover': {
				color: '#aaaaaa',
			},
		},
	}),
	menuPortal: styles => ({ ...styles, zIndex: 9999 }),
	noOptionsMessage: (styles, props) => ({
		...styles,
		color: '#000000',
		cursor: 'default',
		fontWeight: 500,
		textAlign: 'left',
		width: '100%',
		padding:
			props?.selectProps?.size === 'large'
				? 16
				: props?.selectProps?.size === 'small'
				? 8
				: props?.selectProps?.size === 'extra-small'
				? 4
				: 12,
		height:
			props?.selectProps?.size === 'large'
				? 52
				: props?.selectProps?.size === 'small'
				? 36
				: props?.selectProps?.size === 'extra-small'
				? 24
				: 44,
		fontSize:
			props?.selectProps?.size === 'large'
				? 18
				: props?.selectProps?.size === 'small'
				? 14
				: props?.selectProps?.size === 'extra-small'
				? 12
				: 16,
		'.dark &': {
			color: '#e1e1e1',
		},
	}),
	menuList: styles => ({
		...styles,
		padding: 5,
		'::-webkit-scrollbar': {
			padding: '0 5px 0 0',
			width: 6,
		},
		'::-webkit-scrollbar-track': {
			backgroundColor: 'transparent',
		},
		'::-webkit-scrollbar-thumb': {
			backgroundColor: '#018ECC',
			borderRadius: 3,
		},
	}),
	option: (styles, { isSelected, isDisabled, isFocused, selectProps }) => ({
		...styles,
		borderRadius: 10,
		cursor: isDisabled ? 'not-allowed' : 'pointer',
		fontWeight: 500,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		transition: 200,
		whiteSpace: 'nowrap',
		width: '100%',
		padding:
			selectProps?.size === 'large'
				? '14px 16px'
				: selectProps?.size === 'small'
				? '8px 10px'
				: selectProps?.size === 'extra-small'
				? '4px 6px'
				: 12,
		fontSize:
			selectProps?.size === 'large'
				? 18
				: selectProps?.size === 'small'
				? 14
				: selectProps?.size === 'extra-small'
				? 12
				: 16,
		color: isDisabled
			? '#696f85'
			: isSelected
			? '#ffffff'
			: isFocused
			? '#292b32'
			: '#292b32',
		height:
			selectProps?.size === 'large'
				? 52
				: selectProps?.size === 'small'
				? 36
				: selectProps?.size === 'extra-small'
				? 24
				: 44,
		backgroundColor: isDisabled
			? '#f7f8fc'
			: isSelected
			? '#018ECC'
			: isFocused
			? '#64d1ffd7'
			: '#ffffff',
		':hover': {
			backgroundColor: isDisabled
				? '#f7f8fc'
				: isSelected
				? '#018ECC'
				: '#64d1ffd7',
		},
		'.dark &': {
			color: '#ffffff',
			backgroundColor: isDisabled
				? '#333333'
				: isSelected
				? '#018ECC'
				: isFocused
				? '#64d1ffd7'
				: 'transparent',
			':hover': {
				backgroundColor: isDisabled
					? '#333333'
					: isSelected
					? '#018ECC'
					: '#64d1ffd7',
			},
		},
	}),
}
const Select = memo(
	({
		'data-cy': dataCY,
		hideBorder = false,
		isClearable = true,
		isDisabled = false,
		isError = false,
		isSearchable = true,
		menuWidth,
		noOptionsMessage = null,
		onChange,
		onFocus,
		options = [],
		placeholder = '',
		size = 'medium',
		value = null,
	}) => {
		const { t } = useTranslation('components')
		// const [focusedIndex, setFocusedIndex] = useState(-1);
		const NoOptionsMessage = noOptionsMessage
			? noOptionsMessage
			: () => t('no-data')
		// const onKeyDown = e => {
		//    if (e.key === 'ArrowDown') {
		//       if (focusedIndex >= options?.length - 1) {
		//          e.preventDefault();
		//       } else {
		//          setFocusedIndex(prev => prev + 1);
		//       }
		//    } else if (e.key === 'ArrowUp') {
		//       if (focusedIndex <= 0) {
		//          e.preventDefault();
		//       } else {
		//          setFocusedIndex(prev => prev - 1);
		//       }
		//    }
		// };
		return (
			<ReactSelect
				{...defaultOptions}
				data-cy={dataCY}
				isClearable={isClearable}
				isDisabled={isDisabled}
				isSearchable={isSearchable}
				noOptionsMessage={NoOptionsMessage}
				onChange={onChange}
				onFocus={onFocus}
				// onKeyDown={onKeyDown}
				// onMenuOpen={() => {
				//    const findIndex = options?.findIndex(
				//       option => option?.value === value?.value,
				//    );
				//    const focusIndex = findIndex === -1 ? 0 : findIndex;
				//    setFocusedIndex(focusIndex);
				// }}
				options={options}
				placeholder={placeholder}
				size={size}
				value={value}
				styles={{
					...styles,
					control: (
						styles,
						{ isFocused, isDisabled, selectProps, ...res }
					) => ({
						...styles,
						backgroundColor: isDisabled ? '#f4f4f4' : '#ffffff',
						borderRadius: 10,
						borderStyle: 'solid',
						borderWidth: hideBorder ? 0 : 1.5,
						boxShadow: 'none',
						color: '#292b32',
						minWidth: 100,
						outline: 'none',
						padding: 0,
						transition: 0,
						width: '100%',
						height:
							selectProps?.size === 'large'
								? 52
								: selectProps?.size === 'small'
								? 36
								: selectProps?.size === 'extra-small'
								? 30
								: 44,
						minHeight:
							selectProps?.size === 'large'
								? 52
								: selectProps?.size === 'small'
								? 36
								: selectProps?.size === 'extra-small'
								? 30
								: 44,
						cursor: isDisabled ? 'default' : isSearchable ? 'text' : 'pointer',
						borderColor: isFocused
							? '#018ECC'
							: isError
							? '#e41d32'
							: '#e1e1e1',
						':hover': { borderColor: '#018ECC' },
						'.dark &': {
							backgroundColor: '#2E2D2D',
							color: '#e1e1e1',
							borderColor: isFocused
								? '#018ECC'
								: isError
								? '#e41d32'
								: '#555555',
							':hover': { borderColor: '#018ECC' },
						},
					}),
					menu: styles => ({
						...styles,
						...(menuWidth ? { width: menuWidth } : {}),
						backgroundColor: '#ffffff',
						border: 'none',
						borderRadius: 12,
						boxShadow:
							'0 1px 20px 0 rgba(13, 46, 105, 0.07), 0 1px 20px 0 rgba(13, 46, 105, 0.07)',
						margin: '0 !important',
						overflow: 'hidden',
						padding: 0,
						'.dark &': {
							backgroundColor: '#333333',
							boxShadow:
								'0 1px 20px 0px rgba(0, 0, 0, 0.07), 0 1px 20px 0px rgba(0, 0, 0, 0.07)',
						},
					}),
				}}
			/>
		)
	}
)
Select.propTypes = {
	'data-cy': string,
	hideBorder: bool,
	isClearable: bool,
	isDisabled: bool,
	isError: bool,
	isSearchable: bool,
	menuWidth: string,
	noOptionsMessage: any,
	onChange: func,
	onFocus: func,
	options: array,
	placeholder: string,
	size: oneOf(['large', 'medium', 'small', 'extra-small']),
	value: any,
}
export default Select
